import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FrameWithSearchBox } from "../common/Frame";
import { DATE_FORMAT, getDateFnsLocale } from "../date-utils";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { addDays, addMonths, format, formatISO, isValid, parse, parseISO, set, startOfDay, subDays, subMonths } from "date-fns";
import { DateRange } from "../common/DateRange";
import { Alert, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import qs from "qs";
import { ChartContainer } from "./Container";
import { ByDevice } from "./parts/ByDevice";
import { MaterialDeviceSummary, MaterialSummary } from "../schemas";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Total } from "./parts/Total";
import { useAxiosFetchMultiple } from "../hooks";
import { getColor } from "./Colors";
import { Summary } from "./parts/Summary";
import ErrorHandler from "../ErrorHandler";

const locale = getDateFnsLocale();

export function ChartOverview() {

  const [searchParam, setSearchParams] = useSearchParams();

  const paramFrom = parse(searchParam.get('from') || '', DATE_FORMAT, new Date());
  const paramTo = parse(searchParam.get('to') || '', DATE_FORMAT, new Date());

  const today = startOfDay(new Date());
  const initFrom = isValid(paramFrom) ? paramFrom : subMonths(addDays(today, 1), 1);
  const initTo = isValid(paramTo) ? paramTo : today;

  const [from, setFrom] = useState<Date | null>(initFrom);
  const [to, setTo] = useState<Date | null>(initTo);

  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const onSearchClicked = () => {
    if (!isValid(from) || !isValid(to)) return;
    setSearchParams(qs.stringify({
      from: from ? format(from, DATE_FORMAT) : undefined,
      to: to ? format(to, DATE_FORMAT) : undefined,
    }),
      { replace: true }
    )
  }

  const onResetClicked = () => {
    setSearchParams("", { replace: true })
    setFrom(subMonths(addDays(today, 1), 1));
    setTo(today);
  }

  const { user } = useAuthenticator();
  const token = user?.getSignInUserSession()?.getIdToken().getJwtToken();

  const url = `${process.env.REACT_APP_AWS_API_GATEWAY_URL}/material/summary`;

  const is_vaild_date_range = (from: Date, to: Date) => from <= to && addMonths(from, 2) >= to;

  const valid_date_range = is_vaild_date_range(initFrom, initTo);

  const deps_func = (config: any): [boolean, any[]] => {
    const from = parseISO(config.params.from), to = parseISO(config.params.to);
    return [is_vaild_date_range(from, subDays(to, 1)), [config.params.from, config.params.to]]
  }

  const { data, loading, error } = useAxiosFetchMultiple<MaterialDeviceSummary, { count: number, items: MaterialDeviceSummary[] }>(
    {
      config: {
        url: `${url}/device`,
        headers: { Authorization: token },
        params: { from: formatISO(initFrom), to: formatISO(addDays(initTo, 1)) }
      },
    },
    (from, to) => ({ limit: to - from, offset: from }),
    (data) => data.count,
    (data) => data.items,
    deps_func
  )

  const { data: dataCounts, loading: loadingCounts, error: errorCounts } = useAxiosFetchMultiple<MaterialSummary, { count: number, items: MaterialSummary[] }>(
    {
      config: {
        url,
        headers: { Authorization: token },
        params: { from: formatISO(initFrom), to: formatISO(addDays(initTo, 1)) }
      },
    },
    (from, to) => ({ limit: to - from, offset: from }),
    (data) => data.count,
    (data) => data.items,
    deps_func
  )

  const [byDeviceSortOrder, setByDeviceSortOrder] = useState<string>('device');

  const materials = data.map(item => ({ id: item.material_id, name: item.material_name }))
  const materialSet = materials?.filter((material, idx) => materials.findIndex(m => m.id === material.id) === idx);
  const colors = Object.fromEntries(materialSet?.map((m, i) => [m.id, getColor(i, 400)]));

  const errorMessage = (error && (error.response?.data?.message || error.message)) || '';
  const errorCode = (error && (error.response?.data?.code || error.code)) || '';

  useEffect(() => {
    if (error || errorCounts) {
      setOpen(true);
    }
  }, [error, errorCounts]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <FrameWithSearchBox
        title="Chart Overview"
        onSearchClicked={onSearchClicked}
        onResetClicked={onResetClicked}
      >
        <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
          <DateRange from={from} setFrom={setFrom} to={to} setTo={setTo} required />
          {
            !valid_date_range &&
            <Grid item xs={12}>
              <Alert severity="error">
                Invalid date range:
                'From' date must be before or equal to 'To' date,
                and the date range must be within 2 months.
              </Alert>
            </Grid>
          }
        </Grid >
        <Grid container spacing={1} sx={{ height: 800 }}>
          <Grid item xs={12}>
            <ChartContainer
              title="By Date"
              content={<Summary from={initFrom} to={initTo} data={dataCounts || []} colors={colors} />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartContainer
              title="By Device"
              content={<ByDevice data={data || []} colors={colors} sortOrder={byDeviceSortOrder} />}
              action={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: 'small' }}>Sort by</Typography>
                  <Select
                    value={byDeviceSortOrder}
                    onChange={e => setByDeviceSortOrder(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="device">Device</MenuItem>
                    <MenuItem value="count">Count</MenuItem>
                  </Select>
                </Stack>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartContainer
              title="Total"
              content={<Total data={dataCounts || []} colors={colors} />}
            />
          </Grid>
        </Grid>
      </FrameWithSearchBox >
      <ErrorHandler open={open} onClose={onClose} errorMessage={errorMessage} errorCode={errorCode} />
    </LocalizationProvider >
  );
}
